import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'select', label: 'Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'file', label: 'File Upload' },
];

const DEFAULT_FIELD = { field_key: '', label: '', field_type: 'text', required: false, options: [], description: '' };

/**
 * Reusable "Add Field" dialog for form/template field management.
 * Used by AdminFormsPage and AdminTemplatePage.
 */
export function AddFieldDialog({ open, onOpenChange, onAdd, contextLabel = '' }) {
  const [field, setField] = useState({ ...DEFAULT_FIELD });

  const handleAdd = () => {
    if (!field.field_key || !field.label) return;
    onAdd({ ...field });
    setField({ ...DEFAULT_FIELD });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-field-dialog">
        <DialogHeader>
          <DialogTitle>Add {contextLabel ? `${contextLabel} ` : ''}Field</DialogTitle>
          {contextLabel && <DialogDescription>Add a new field to the {contextLabel} form</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Field Key</Label>
            <Input value={field.field_key} onChange={e => setField(p => ({ ...p, field_key: e.target.value.replace(/\s/g, '_').toLowerCase() }))} data-testid="new-field-key" placeholder="years_experience" />
          </div>
          <div className="space-y-2">
            <Label>Label</Label>
            <Input value={field.label} onChange={e => setField(p => ({ ...p, label: e.target.value }))} data-testid="new-field-label" placeholder="Years of Experience" />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={field.field_type} onValueChange={v => setField(p => ({ ...p, field_type: v }))}>
              <SelectTrigger data-testid="new-field-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={field.description} onChange={e => setField(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox checked={field.required} onCheckedChange={v => setField(p => ({ ...p, required: v }))} />
            <Label>Required</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAdd} disabled={!field.field_key || !field.label} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-add-field">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Field table for displaying form/template fields with toggle and remove actions.
 */
export function FieldTable({ fields = [], onToggleRequired, onRemove, Table, TableHeader, TableRow, TableHead, TableBody, TableCell, Badge, Button: Btn, Trash2, Checkbox: Chk }) {
  return (
    <div className="rounded-lg border bg-card">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Key</TableHead><TableHead>Label</TableHead><TableHead>Type</TableHead>
          <TableHead>Required</TableHead><TableHead>Description</TableHead><TableHead>Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {fields.map((f, i) => (
            <TableRow key={i} data-testid={`form-field-${f.field_key}`}>
              <TableCell className="font-mono text-xs">{f.field_key}</TableCell>
              <TableCell className="font-medium text-sm">{f.label}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs">{f.field_type}</Badge></TableCell>
              <TableCell><Chk checked={f.required} onCheckedChange={() => onToggleRequired(i)} /></TableCell>
              <TableCell className="text-xs text-muted-foreground">{f.description || '-'}</TableCell>
              <TableCell><Btn variant="ghost" size="icon" onClick={() => onRemove(i)} className="h-7 w-7 text-destructive"><Trash2 className="w-3.5 h-3.5" /></Btn></TableCell>
            </TableRow>
          ))}
          {fields.length === 0 && (
            <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No fields configured. Add a field to get started.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
